import { useEffect, useState } from 'react';
import { api, datum, type Anmelder } from '../api.ts';
import {
  Dialog, Fehlerkasten, Text, ausFehler, type Fehlerlage,
} from './Formular.tsx';
import { Leer } from './Geruest.tsx';
import { useFrisch } from './Frisch.tsx';
import { fragen } from './rueckfrage.tsx';

/**
 * Aufmaß und Montage-Doku freigeben oder abschliessen.
 *
 * ORIGINALTEXT MARVIN (24.08.2026)
 *   „und generell will ich, dass du nun alles einmal fertig programmierst,
 *    schaue dir dazu die vorlagen an und baue alle punkte 1:1 NEU in dem
 *    neuen system."
 *
 * Früher waren das vier Menüpunkte. Hier ist es ein Fenster mit beiden
 * Ständen nebeneinander: es ist dieselbe Frage, nur zweimal, und wer den
 * einen Stand ändert, will meistens auch wissen, wo der andere steht.
 *
 * BEIM FREIGEBEN GEHÖRT DER GRUND DAZU. Wer etwas wieder aufmacht, das ein Sub
 * schon abgegeben hat, schuldet ihm den Satz warum: sonst sieht der Sub am
 * nächsten Morgen eine offene Aufgabe und weiss nicht, was er anders machen
 * soll.
 */
const STAND_TEXT: Record<string, string> = {
  offen: 'offen', eingereicht: 'eingereicht', abgeschlossen: 'abgeschlossen',
};

const STAND_MARKE: Record<string, string> = {
  offen: 'z-neu', eingereicht: 'z-laufend', abgeschlossen: 'z-erledigt',
};

export function DokuDialog({
  auftragId, baustelle, zu, fertig,
}: {
  auftragId: string; baustelle?: string; zu: () => void; fertig: () => void;
}) {
  const [d, setD] = useState<any>(null);
  const [grund, setGrund] = useState('');
  const [arbeitet, setArbeitet] = useState<string | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  async function laden() { setD(await api.doku(auftragId)); }
  useEffect(() => { laden().catch((f) => setFehler(ausFehler(f))); }, [auftragId]);
  useFrisch(laden);

  async function schritt(was: 'aufmass' | 'montage',
                         welcher: 'freigeben' | 'abschliessen') {
    setArbeitet(was + welcher); setFehler(null);
    try {
      /* ERST FRAGEN, DANN PUSHEN (.358). */
      const push = welcher === 'freigeben' ? await fragen('Die Firma per Telegram informieren, dass wieder freigegeben ist?\n\nOK = ja, pushen\nAbbrechen = nein, nicht pushen') : false;
      await api.dokuSchritt(auftragId, was, welcher, grund || null, push);
      setGrund('');
      await laden();
      fertig();
    } catch (f) { setFehler(ausFehler(f)); } finally { setArbeitet(null); }
  }

  return (
    <Dialog titel="Freigeben und Abschließen" untertitel={baustelle}
            breite={680} laeuft={!!arbeitet} zu={zu}>
      <Fehlerkasten lage={fehler} />
      {!d ? <Leer text="Wird geladen." /> : (
        <>
          {d.einblasstop_offen && (
            <div className="hinweis warnung">
              <b>Für diesen Auftrag ist ein Einblasstop gemeldet.</b>
              <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
                3 Montageordner sind deshalb von der Pflicht freigestellt.
                Wird ein neuer Montagetermin auf heute oder später gesetzt,
                nimmt das System die Meldung zurück und öffnet die
                Montage-Dokumentation wieder.
              </div>
            </div>
          )}

          {d.aufmass.noetig === false && (
            <div className="hinweis">
              <b>Kein Aufmaß nötig.</b>
              <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
                {d.aufmass.ohne_grund} Der Auftrag geht mit der eingereichten
                Montage-Dokumentation weiter zum Abrechnen.
              </div>
            </div>
          )}

          <table className="liste">
            <thead>
              <tr>
                <th style={{ width: 230 }}>Dokumentation</th>
                <th style={{ width: 170 }}>Stand</th>
                <th>Was hier geht</th>
              </tr>
            </thead>
            <tbody>
              {([['aufmass', 'Aufmaß', 'Die Mengen des Tiefbauers'],
                 ['montage', 'Montage-Dokumentation',
                  'Die Nachweisfotos des Monteurs']] as const)
                /* OHNE TIEFBAU KEINE AUFMASSZEILE (05.10.2026). Steht das
                   Aufmaß trotzdem nicht auf offen, bleibt sie sichtbar:
                   was eingereicht ist, soll sich auch freigeben lassen. */
                .filter(([was]) => was !== 'aufmass' || d.aufmass.noetig !== false
                                   || d.aufmass.stand !== 'offen')
                .map(([was, name, zweck]) => {
                  const s = d[was];
                  return (
                    <tr key={was} style={{ cursor: 'default' }}>
                      <td>
                        <b>{name}</b>
                        <div className="klein leise">{zweck}</div>
                      </td>
                      <td>
                        <span className={'marke-zustand ' + (STAND_MARKE[s.stand] ?? '')}>
                          {STAND_TEXT[s.stand] ?? s.stand}
                        </span>
                        {s.eingereicht_am && (
                          <div className="klein leise">
                            eingereicht {datum(s.eingereicht_am)}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="reihe">
                          <button className="knopf klein"
                                  disabled={!s.freigeben.erlaubt || !!arbeitet}
                                  title={s.freigeben.erlaubt
                                    ? 'Der Sub kann wieder bearbeiten.'
                                    : `${name} ${s.freigeben.grund}`}
                                  onClick={() => schritt(was, 'freigeben')}>
                            Freigeben
                          </button>
                          <button className="knopf klein"
                                  disabled={!s.abschliessen.erlaubt || !!arbeitet}
                                  title={s.abschliessen.erlaubt
                                    ? (s.abschliessen.hinweis ?? 'Endgültig zu.')
                                    : `${name} ${s.abschliessen.grund}`}
                                  onClick={() => schritt(was, 'abschliessen')}>
                            Abschließen
                          </button>
                        </div>
                        {/* Ein gesperrter Knopf nennt seinen Grund. */}
                        {!s.freigeben.erlaubt && (
                          <div className="klein leise" style={{ marginTop: 4 }}>
                            {name} {s.freigeben.grund}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>

          <div style={{ marginTop: 14 }}>
            <Text name="grund" titel="Grund" mehrzeilig
                  wert={grund} setze={setGrund} fehler={fehler}
                  hinweis="Beim Freigeben Pflicht: der Sub sieht ihn. Ohne ihn
                           weiss er nicht, was er anders machen soll, und gibt
                           dasselbe noch einmal ab. Beim Abschließen
                           freiwillig." />
          </div>
        </>
      )}
    </Dialog>
  );
}


/**
 * Der WFMT-Verlauf: der Langtext der Telekom, in Einträge zerlegt.
 *
 * ORIGINALTEXT MARVIN (04.08.2026)
 *   „warum steht der neuste eintrag im wfmt verlauf unten. das ist falsch rum
 *    soriert."
 *
 * Sortiert wird nach dem DATUM, nicht nach der Reihenfolge im Text: auf die
 * ist kein Verlass, sie ist die der Telekom und nicht unsere.
 */
export function WfmtDialog({
  auftragId, baustelle, zu,
}: { auftragId: string; baustelle?: string; zu: () => void }) {
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  useEffect(() => {
    api.wfmt(auftragId).then(setD).catch((f) => setFehler(ausFehler(f)));
  }, [auftragId]);

  return (
    <Dialog titel="WFMT-Verlauf" untertitel={baustelle} breite={760} zu={zu}>
      <Fehlerkasten lage={fehler} />
      {!d ? <Leer text="Wird gelesen." /> : (
        <>
          <p className="klein leise" style={{ marginTop: 0 }}>
            Aus dem Vorsystem der Telekom, neuester Eintrag zuerst.
            {d.anzahl > 0
              && ` ${d.anzahl} ${d.anzahl === 1 ? 'Eintrag' : 'Einträge'}.`}
          </p>
          {d.hinweis && <div className="hinweis">{d.hinweis}</div>}
          {d.roh && (
            <pre className="klein" style={{ whiteSpace: 'pre-wrap',
                                            margin: 0 }}>{d.roh}</pre>
          )}
          {d.eintraege.map((e: any, i: number) => (
            <div key={i} className="karte" style={{ marginBottom: 8, borderLeft: e.neu ? '4px solid var(--ueberfaellig)' : undefined }}>
              <div className="leib">
                <div className="klein leise">
                  {e.neu && <b style={{ color: 'var(--ueberfaellig)', marginRight: 6 }}>NEU</b>}
                  {e.datum || 'ohne Datum'}
                  {e.verfasser && `, ${e.verfasser}`}
                </div>
                <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>
                  {e.inhalt || e.text}
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </Dialog>
  );
}
